import { format, getMonth, isSameDay } from 'date-fns';
import html2pdf from 'html-to-pdf-js';
import { useCallback, useMemo, useState } from 'react';

import { Device } from '../../enums';
import { getPatientName, usePatientByUrlParam } from '../patients';
import { useDateRange } from '../physiological-data';

export const useExportToPdf = () => {
  const patient = usePatientByUrlParam();
  const { dateFrom, dateTo, rangeType } = useDateRange();
  const [isLoading, setIsLoading] = useState(false);

  const patientName = useMemo(() => getPatientName(patient).replace(/\W/, ''), [patient]);
  const reportTimespan = useMemo(() => {
    const dateFromFormat = getMonth(dateFrom) === getMonth(dateTo) ? 'dd' : 'ddMMMyyyy';

    return isSameDay(dateFrom, dateTo)
      ? format(dateTo, 'ddMMMyyyy')
      : `${format(dateFrom, dateFromFormat)}-${format(dateTo, 'ddMMMyyyy')}`;
  }, [dateFrom, dateTo]);

  const exportPhysiologicalDataToPdf = useCallback(
    async (selectedDevices: Device[]) => {
      const today = format(Date.now(), 'ddMMMyyyy');
      const filename = `Aktiia_BP_${today}_${patientName}_${rangeType}_${reportTimespan}`;

      setIsLoading(true);
      const input = document.getElementById('patientPhysiologicalDataWrapper') as HTMLDivElement | null;
      if (input) {
        const spans = input.querySelectorAll('span:not(.custom-tick)') as NodeListOf<HTMLSpanElement>;
        const filteredSpans = input.querySelectorAll(
          'span:not(.rounded-full):not(.custom-tick):not(.status)',
        ) as NodeListOf<HTMLSpanElement>;
        const buttons = input.querySelectorAll(
          'button:not(.bloodPressureClassInfoBtn)',
        ) as NodeListOf<HTMLButtonElement>;
        const learnMoreButton = input.querySelector('.learn-more-btn') as HTMLButtonElement | null;
        const pdfHide = input.querySelectorAll('.pdf-hide') as NodeListOf<HTMLButtonElement>;
        const svgs = input.querySelectorAll('svg') as NodeListOf<SVGElement>;
        const chevrons = input.querySelectorAll('.chevron') as NodeListOf<HTMLImageElement>;
        const actionsForPatient = input.querySelector('.actions-for-patient') as HTMLDivElement | null;
        const sendMessageButton = input.querySelector('.send-message-button') as HTMLButtonElement | null;
        const stickyHeader = document.getElementById('stickyHeader') as HTMLDivElement | null;
        const customTickWrapper = document.querySelector('.custom-tick-wrapper') as HTMLDivElement | null;
        const patientSummary = document.querySelector('.patient-summary') as HTMLDivElement | null;
        const detailedBpBraceletList = document.getElementById('detailedBpBraceletList') as HTMLDivElement | null;
        const detailedBpBraceletTable = document.getElementById('detailedBpBraceletTable') as HTMLDivElement | null;
        const detailedBpCuffList = document.getElementById('detailedBpCuffList') as HTMLDivElement | null;
        const detailedBpCuffListSubHeader = input.querySelector(
          '.detailedBpCuffListSubHeader',
        ) as HTMLDivElement | null;
        const detailedBpCuffTable = document.getElementById('detailedBpCuffTable') as HTMLDivElement | null;

        const isDetailedBpBraceletTableHidden = detailedBpBraceletTable?.classList.contains('hidden');
        const isDetailedBpCuffTableHidden = detailedBpCuffTable?.classList.contains('hidden');

        if (selectedDevices.includes(Device.BRACELET)) {
          detailedBpBraceletTable?.classList.remove('hidden');
        } else {
          detailedBpBraceletList?.classList.add('hidden');
        }

        if (selectedDevices.includes(Device.CUFF)) {
          detailedBpCuffTable?.classList.remove('hidden');
        } else {
          detailedBpCuffList?.classList.add('hidden');
        }

        const dataInputWidth = input.style.width;
        input.style.width = '1920px';

        if (navigator.userAgent.toLowerCase().indexOf('firefox') > -1) {
          input.style.wordSpacing = '0.5rem';
        } else {
          input.style.wordSpacing = '0.25rem';
        }

        const chart = input.querySelector('.recharts-surface') as SVGElement | null;
        const chartWrapper = input.querySelector('.recharts-wrapper') as HTMLDivElement | null;
        chart?.setAttribute('key', 'recharts-surface');
        const chartWidth = chart?.style.width ?? '';
        const chartHeight = chart?.style.height ?? '';
        const chartWrapperHeight = chartWrapper?.style.height ?? '';
        if (chart) {
          chart.style.width = '1840px';
          chart.style.height = 'auto';
        }
        if (chartWrapper) {
          chartWrapper.style.height = 'auto';
        }
        if (customTickWrapper) {
          customTickWrapper.style.height = '50px';
          customTickWrapper.style.maxHeight = '50px';
        }

        for (let i = 0; i < spans.length; i++) {
          spans[i].style.paddingBottom = '12px';
        }
        for (let i = 0; i < filteredSpans.length; i++) {
          filteredSpans[i].style.paddingRight = '30px';
        }

        for (let i = 0; i < buttons.length; i++) {
          buttons[i].style.paddingBottom = '15px';
        }

        for (let i = 0; i < svgs.length; i++) {
          svgs[i].style.marginBottom = '-12px';
        }

        for (let i = 0; i < chevrons.length; i++) {
          chevrons[i].style.visibility = 'hidden';
        }

        for (let i = 0; i < pdfHide.length; i++) {
          pdfHide[i].style.visibility = 'hidden';
        }

        actionsForPatient?.classList.add('hidden');
        sendMessageButton?.classList.add('hidden');
        learnMoreButton?.classList.add('hidden');
        if (detailedBpCuffListSubHeader) {
          detailedBpCuffListSubHeader.style.display = 'none';
        }
        patientSummary?.classList.add('mb-[-35px]');

        const stickyClasses =
          'fixed top-16 z-[5] left-60 ml-6 px-6 pt-0 h-19 items-center right-6 bg-background-base-primary h-[4.75rem] drop-shadow-stickyHeader';

        if (stickyHeader) {
          stickyClasses.split(' ').map((className) => stickyHeader.classList.remove(className));
        }

        const htmlWidth = input.clientWidth;
        const htmlHeight = input.clientHeight;
        const pdfWidth = htmlWidth / 0.85;
        const pdfHeight = htmlWidth * 1.5;

        const opts = {
          filename,
          html2canvas: { allowTaint: true, width: htmlWidth, height: htmlHeight },
          jsPDF: { unit: 'px', orientation: 'portrait', format: [pdfWidth, pdfHeight], hotfixes: ['px_scaling'] },
        };
        await html2pdf().set(opts).from(input).save();

        for (let i = 0; i < spans.length; i++) {
          spans[i].style.paddingBottom = '0';
        }

        for (let i = 0; i < filteredSpans.length; i++) {
          filteredSpans[i].style.paddingRight = '0';
        }

        for (let i = 0; i < buttons.length; i++) {
          buttons[i].style.paddingBottom = '0';
        }

        for (let i = 0; i < svgs.length; i++) {
          svgs[i].style.marginBottom = '0';
        }

        for (let i = 0; i < chevrons.length; i++) {
          chevrons[i].style.visibility = 'visible';
        }

        for (let i = 0; i < pdfHide.length; i++) {
          pdfHide[i].style.visibility = 'visible';
        }

        actionsForPatient?.classList.remove('hidden');
        sendMessageButton?.classList.remove('hidden');
        learnMoreButton?.classList.remove('hidden');
        if (detailedBpCuffListSubHeader) {
          detailedBpCuffListSubHeader.style.display = 'block';
        }

        detailedBpBraceletList?.classList.remove('hidden');
        if (isDetailedBpBraceletTableHidden) {
          detailedBpBraceletTable?.classList.add('hidden');
        }

        detailedBpCuffList?.classList.remove('hidden');
        if (isDetailedBpCuffTableHidden) {
          detailedBpCuffTable?.classList.add('hidden');
        }

        if (stickyHeader) {
          stickyClasses.split(' ').map((className) => stickyHeader.classList.add(className));
        }

        if (chart) {
          chart.style.width = chartWidth;
          chart.style.height = chartHeight;
        }
        if (chartWrapper) {
          chartWrapper.style.height = chartWrapperHeight;
        }
        if (customTickWrapper) {
          customTickWrapper.style.marginBottom = 'inherit';
        }
        input.style.width = dataInputWidth;
        input.style.wordSpacing = 'inherit';
        patientSummary?.classList.remove('mb-[-35px]');

        setIsLoading(false);
      } else {
        console.error('Error while generating PDF.');
        setIsLoading(false);
      }
    },
    [rangeType],
  );

  return { exportPhysiologicalDataToPdf, isLoading };
};
